import { useRef, useEffect, useState, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { usePannellum, type PannellumViewer, type PannellumConfig } from '../../hooks/usePannellum';

interface PanoramaViewerProps {
  imageUrl: string;
  title?: string;
  autoRotate?: boolean;
  showControls?: boolean;
  height?: string;
  className?: string;
  onLoad?: () => void;
  onError?: (error: string) => void;
}

export default function PanoramaViewer({
  imageUrl,
  title,
  autoRotate = false,
  showControls = true,
  height = '400px',
  className = '',
  onLoad,
  onError,
}: PanoramaViewerProps) {
  const { t, i18n } = useTranslation(['property', 'common']);
  const direction = i18n.dir();
  const containerRef = useRef<HTMLDivElement>(null);
  const viewerRef = useRef<PannellumViewer | null>(null);
  const { isLoaded, isLoading, error: loadError, createViewer } = usePannellum();
  const [viewerError, setViewerError] = useState<string | null>(null);
  const [isViewerReady, setIsViewerReady] = useState(false);

  // Cleanup viewer on unmount
  useEffect(() => {
    return () => {
      if (viewerRef.current) {
        try {
          viewerRef.current.destroy();
        } catch {
          // Ignore cleanup errors
        }
        viewerRef.current = null;
      }
    };
  }, []);

  // Initialize viewer when Pannellum is loaded
  useEffect(() => {
    if (!isLoaded || !containerRef.current || !imageUrl) {
      return;
    }

    // Destroy existing viewer
    if (viewerRef.current) {
      try {
        viewerRef.current.destroy();
      } catch {
        // Ignore cleanup errors
      }
      viewerRef.current = null;
    }

    setViewerError(null);
    setIsViewerReady(false);

    const config: PannellumConfig = {
      type: 'equirectangular',
      panorama: imageUrl,
      autoLoad: true,
      autoRotate: autoRotate ? -2 : 0,
      compass: false,
      showControls,
      showFullscreenCtrl: showControls,
      showZoomCtrl: showControls,
      mouseZoom: true,
      draggable: true,
      hfov: 100,
      minHfov: 50,
      maxHfov: 120,
      title: title,
      strings: {
        loadButtonLabel: t('virtualTour.loading', { defaultValue: 'Loading virtual tour...' }),
        loadingLabel: t('virtualTour.loading', { defaultValue: 'Loading...' }),
        noPanoramaError: t('virtualTour.error', { defaultValue: 'Unable to load virtual tour' }),
        fileAccessError: t('virtualTour.error', { defaultValue: 'Unable to load virtual tour' }),
        malformedURLError: t('virtualTour.error', { defaultValue: 'Invalid tour URL' }),
        genericWebGLError: t('virtualTour.error', { defaultValue: 'WebGL is not supported' }),
      },
    };

    try {
      const viewer = createViewer(containerRef.current, config);
      if (viewer) {
        viewerRef.current = viewer;

        // Handle load event
        viewer.on('load', () => {
          setIsViewerReady(true);
          onLoad?.();
        });

        // Handle error event
        viewer.on('error', () => {
          const errorMsg = t('virtualTour.error', { defaultValue: 'Unable to load virtual tour' });
          setViewerError(errorMsg);
          onError?.(errorMsg);
        });
      }
    } catch (err) {
      const errorMsg = err instanceof Error ? err.message : 'Failed to create viewer';
      setViewerError(errorMsg);
      onError?.(errorMsg);
    }
  }, [isLoaded, imageUrl, autoRotate, showControls, title, createViewer, onLoad, onError, t]);

  // Handle auto-rotate toggle
  const toggleAutoRotate = useCallback(() => {
    if (viewerRef.current) {
      if (autoRotate) {
        viewerRef.current.stopAutoRotate();
      } else {
        viewerRef.current.startAutoRotate(-2);
      }
    }
  }, [autoRotate]);

  const displayError = loadError || viewerError;

  if (displayError) {
    return (
      <div
        className={`flex items-center justify-center bg-gray-100 dark:bg-[var(--pv-color-surface-soft)] rounded-xl ${className}`}
        style={{ height }}
        dir={direction}
      >
        <div className="text-center p-4">
          <i className="ri-error-warning-line text-4xl text-red-400 mb-2"></i>
          <p className="text-sm text-gray-600 dark:text-[var(--pv-color-text-muted)]">{displayError}</p>
        </div>
      </div>
    );
  }

  return (
    <div className={`relative ${className}`} style={{ height }} dir={direction}>
      {/* Panorama container */}
      <div
        ref={containerRef}
        className="w-full h-full rounded-xl overflow-hidden"
        style={{
          background: 'linear-gradient(135deg, #002A23 0%, #001713 100%)',
        }}
      />

      {/* Loading overlay */}
      {(isLoading || !isViewerReady) && (
        <div className="absolute inset-0 flex items-center justify-center bg-gray-900/50 rounded-xl">
          <div className="text-center">
            <i className="ri-loader-4-line text-4xl text-white animate-spin mb-2"></i>
            <p className="text-sm text-white/80">
              {t('virtualTour.loading', { defaultValue: 'Loading virtual tour...' })}
            </p>
          </div>
        </div>
      )}

      {/* 360 badge */}
      <div className="absolute top-3 left-3 z-10">
        <span className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-[var(--pv-color-primary)] text-white text-xs font-medium shadow-lg">
          <i className="ri-panorama-2-line"></i>
          {t('virtualTour.badge', { defaultValue: '360°' })}
        </span>
      </div>

      {/* Custom controls overlay */}
      {showControls && isViewerReady && (
        <div className="absolute bottom-3 right-3 z-10 flex gap-2">
          <button
            type="button"
            onClick={toggleAutoRotate}
            className="p-2 rounded-lg bg-white/90 dark:bg-[var(--pv-color-surface-strong)]/90 shadow-lg hover:bg-white dark:hover:bg-[var(--pv-color-surface-strong)] transition-colors"
            title={t('virtualTour.controls.autoRotate', { defaultValue: 'Auto rotate' })}
          >
            <i className="ri-refresh-line text-gray-700 dark:text-[var(--pv-color-text)]"></i>
          </button>
        </div>
      )}
    </div>
  );
}
